import React, { forwardRef } from 'react';
import { keyCode } from '../../helpers';
import { useTooltip, useGlobalState } from '../../hooks';
import { Icon, Label } from '../';

import './Checkbox.css';

/**
 * A styled checkbox input with optional label and tooltip.
 *
 * The tooltip description (`title`) falls back to the `displayNames` global
 * state entry for the given `name` key when `title` is not explicitly provided:
 *   - `title` falls back to `displayNames[name]?.display_name_description`
 *
 * @param {object} props
 * @param {string} [props.name]                - Field name; also used as displayNames lookup key.
 * @param {function} props.onChange            - Change handler.
 * @param {boolean|'multiple'} props.checked   - Checked state; `'multiple'` renders an indeterminate style.
 * @param {string} [props.label]               - Label text rendered beside the checkbox.
 * @param {boolean} [props.disabled=false]     - Disables the checkbox when true.
 * @param {string} [props.className]           - Additional CSS class names.
 * @param {string} [props.title]               - Tooltip description shown on hover. Falls back to displayNames.
 * @param {string} [props.labelPosition]       - CSS modifier suffix for label placement.
 * @param {string} [props.icon]                - Icon name to render in place of the default checkmark.
 * @param ref - Forwarded ref attached to the checkmark element.
 */
const _Checkbox = (
  { name, onChange, checked, label, disabled = false, className, title, labelPosition, icon },
  ref
) => {
  const { showTooltip, hideTooltip } = useTooltip();
  const [displayNames] = useGlobalState('displayNames');
  title = title ?? displayNames[name]?.display_name_description;

  const handleKeyDown = e => {
    switch (e.keyCode) {
      case keyCode.ENTER:
      case keyCode.SPACE:
        onChange();
        break;

      default:
        break;
    }
  };

  return (
    <label
      className={
        'CustomCheckbox' +
        (className ? ' ' + className : '') +
        (disabled ? ' disabled' : '') +
        (labelPosition ? ' label-position-' + labelPosition : '')
      }
      tabIndex={0}
      onKeyDown={handleKeyDown}
      ref={ref}
      onMouseEnter={() => title && showTooltip({ title: title })}
      onMouseLeave={() => title && hideTooltip()}
    >
      <input
        type="checkbox"
        name={name}
        onChange={onChange}
        checked={checked ? true : false}
        disabled={disabled}
      />
      {icon ? (
        <span className="checkmark-icon-wrapper">
          <span className={'checkmark-icon' + (checked === 'multiple' ? ' null' : '')}>
            <Icon name={icon} />
          </span>
        </span>
      ) : (
        <span className={'checkmark' + (checked === 'multiple' ? ' null' : '')} ref={ref}></span>
      )}
      <Label
        label={label}
        //className={isActive ? 'active' : ''}
        htmlFor={name}
        title={title}
        disabled={disabled}
        onClick={() => onChange({ target: { name: name, type: 'checkbox', checked: !checked } })}
        //isOptional={isOptional}
      />

      {/*label && (
        <span className="label">
          {label}
          {title && <Icon name="info-small" />}
        </span>
      )*/}
    </label>
  );
};

export const Checkbox = forwardRef(_Checkbox);
